import { useAppSelector } from '@app/store';
import { useMemo } from 'react';

import {
  selectCaptionSettings,
  selectRecordingPlayback,
  selectRecordingTranscriptSegments,
  selectSelectedClip,
} from '../selector';
import { CAPTION_FONT_OPTIONS } from '../utils';

// Number of words to show at a time in caption chunks
const WORDS_PER_CHUNK = 5;

export type CaptionPositionKey = 'top' | 'center' | 'bottom';

// Bottom position: two offsets so caption can adapt when control bar is visible vs hidden
const BOTTOM_WITH_CONTROL_BAR = 'bottom-[18%]';
const BOTTOM_WITHOUT_CONTROL_BAR = 'bottom-[9%]';

const POSITION_CLASSES: Record<CaptionPositionKey, string> = {
  top: 'top-[6%]',
  center: 'top-1/2 -translate-y-1/2',
  bottom: BOTTOM_WITH_CONTROL_BAR,
};

export const getCaptionPositionClass = (
  position: CaptionPositionKey,
  controlsBarVisible: boolean,
): string => {
  if (position === 'bottom') {
    return controlsBarVisible
      ? BOTTOM_WITH_CONTROL_BAR
      : BOTTOM_WITHOUT_CONTROL_BAR;
  }
  return POSITION_CLASSES[position];
};

export type ActiveCaptionResult = {
  text: string | null;
  words: string[];
  activeWordIndex: number;
  highlightColor: string;
  fontFamily: string;
  fontWeight: string;
  fontScale: number;
  /** Position key for bottom offset override when control bar visibility changes */
  captionsPosition: CaptionPositionKey;
};

/**
 * Calculates which word in the full segment is active based on current time.
 * Uses character-weighted timing - longer words get proportionally more time.
 * This better approximates natural speech patterns where longer words take
 * more time to pronounce.
 *
 * @returns The index of the active word in the full segment word list
 */
export const calculateActiveWordIndexInSegment = (
  segmentStart: number,
  segmentEnd: number,
  currentTime: number,
  allWords: string[],
): number => {
  if (allWords.length === 0) return -1;
  if (allWords.length === 1) return 0;

  const segmentDuration = segmentEnd - segmentStart;
  if (segmentDuration <= 0) return 0;

  // Calculate total character count for weighting
  // Add 1 per word to account for natural pauses between words
  const wordWeights = allWords.map(word => word.length + 1);
  const totalWeight = wordWeights.reduce((sum, w) => sum + w, 0);

  // Calculate cumulative time boundaries for each word
  const timeIntoSegment = Math.max(0, currentTime - segmentStart);

  let accumulatedWeight = 0;
  for (let i = 0; i < allWords.length; i++) {
    accumulatedWeight += wordWeights[i];
    const wordEndTime = (accumulatedWeight / totalWeight) * segmentDuration;

    if (timeIntoSegment <= wordEndTime) {
      return i;
    }
  }

  return allWords.length - 1;
};

/**
 * Splits words into chunks and determines which chunk to display
 * based on the active word index.
 *
 * @returns The current chunk of words and the active word index within that chunk
 */
export const getActiveChunk = (
  allWords: string[],
  activeWordInSegment: number,
): { chunkWords: string[]; activeIndexInChunk: number } => {
  if (allWords.length === 0 || activeWordInSegment < 0) {
    return { chunkWords: [], activeIndexInChunk: -1 };
  }

  // Calculate which chunk the active word belongs to
  const chunkIndex = Math.floor(activeWordInSegment / WORDS_PER_CHUNK);
  const startIndex = chunkIndex * WORDS_PER_CHUNK;
  const endIndex = Math.min(startIndex + WORDS_PER_CHUNK, allWords.length);

  // Get the words for this chunk
  const chunkWords = allWords.slice(startIndex, endIndex);

  // Calculate the active word index within this chunk
  const activeIndexInChunk = activeWordInSegment - startIndex;

  return { chunkWords, activeIndexInChunk };
};

/**
 * Hook to get the active caption based on current playback time.
 * Returns the caption text, font styling, position, and active word highlighting.
 * In clip mode, only shows captions within clip boundaries.
 */
export const useActiveCaption = (): ActiveCaptionResult => {
  const segments = useAppSelector(selectRecordingTranscriptSegments);
  const playback = useAppSelector(selectRecordingPlayback);
  const selectedClip = useAppSelector(selectSelectedClip);
  const captionSettings = useAppSelector(selectCaptionSettings);

  // Get font styling from caption settings
  const fontOption =
    CAPTION_FONT_OPTIONS.find(f => f.id === captionSettings.captions_style) ||
    CAPTION_FONT_OPTIONS[0];

  // Get position key (overlay uses getCaptionPositionClass(captionsPosition, controlsBarVisible))
  const captionsPosition =
    (captionSettings.captions_position as CaptionPositionKey) || 'bottom';

  // Get highlight color from caption settings
  const highlightColor = captionSettings.highlight_color;

  // Find active segment and calculate active word
  const { activeSegment, currentTimeSeconds } = useMemo(() => {
    if (!segments || segments.length === 0) {
      return { activeSegment: null, currentTimeSeconds: 0 };
    }

    const currentTimeMs = playback.currentTime * 1000;
    const currentTimeSec = playback.currentTime;

    // In clip mode, only show captions within clip boundaries
    if (selectedClip) {
      const clipStart = selectedClip.start_time_ms;
      const clipEnd = selectedClip.end_time_ms;

      // If current time is outside clip boundaries, no caption
      if (currentTimeMs < clipStart || currentTimeMs > clipEnd) {
        return { activeSegment: null, currentTimeSeconds: currentTimeSec };
      }
    }

    // Find segment where current time falls within start and end
    const segment =
      segments.find(
        seg =>
          currentTimeMs >= seg.start * 1000 && currentTimeMs <= seg.end * 1000,
      ) || null;

    return { activeSegment: segment, currentTimeSeconds: currentTimeSec };
  }, [segments, playback.currentTime, selectedClip]);

  // Split text into words, calculate active word, and get current chunk
  const { words, activeWordIndex } = useMemo(() => {
    if (!activeSegment?.text) {
      return { words: [], activeWordIndex: -1 };
    }

    // Get all words from the segment
    const allWords = activeSegment.text.split(/\s+/).filter(w => w.length > 0);

    // Calculate which word in the full segment is currently active
    const activeWordInSegment = calculateActiveWordIndexInSegment(
      activeSegment.start,
      activeSegment.end,
      currentTimeSeconds,
      allWords,
    );

    // Get the current chunk of words and active index within chunk
    const { chunkWords, activeIndexInChunk } = getActiveChunk(
      allWords,
      activeWordInSegment,
    );

    return { words: chunkWords, activeWordIndex: activeIndexInChunk };
  }, [activeSegment, currentTimeSeconds]);

  return {
    text: activeSegment?.text || null,
    words,
    activeWordIndex,
    highlightColor,
    fontFamily: fontOption.fontFamily,
    fontWeight: fontOption.fontWeight,
    fontScale: fontOption.fontScale,
    captionsPosition,
  };
};
